import React, { useEffect, useMemo, useState } from 'react';
import CardCommon from '@sg/components/CardCommon';
import IconTipsCom from '@sg/components/IconTipsCom';
import NumArrow from '@/pages/intelligence/pages/TopCharts/components/DetailsPage/NumArrow';
import Loading from '@intelligence/components/Loading';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';
import { getNumConfig } from '@sg/utils/common';
import { formatMainValue } from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/utils';
import { GGG_OVERVIEW_TIPS } from '../../../const';
import type {
  IGggFinanceSummary,
  IGggFinanceSummarySection,
} from '../../../types';
import './index.scss';

interface IFinanceOverviewCardProps {
  summary?: IGggFinanceSummary | null;
  studioCover?: string;
  studioName?: string;
  loading?: boolean;
}

const CLS = 'dbs-ltf-overview-card';

const formatMetricValue = (
  value?: number,
  withCurrency = false,
): string =>
  formatMainValue(value, 'db_bmk0', withCurrency ? 'USD' : undefined);

const MetricBlock: React.FC<{
  title: string;
  value?: number;
  mom?: number;
  withCurrency?: boolean;
}> = ({ title, value, mom, withCurrency = false }) => {
  const newValue =
    mom !== undefined && mom !== null ? String(Number(mom).toFixed(2)) : '';
  const numArrowData = getNumConfig(newValue, 0);

  return (
    <div className={`${CLS}__metric`}>
      <div className={`${CLS}__metric-title`}>{title}</div>
      <div className={`${CLS}__metric-value`}>
        {formatMetricValue(value, withCurrency)}
      </div>
      <div className={`${CLS}__mom`}>
        <span className={`${CLS}__mom-label`}>MoM</span>
        {numArrowData ? (
          <NumArrow dataContent={numArrowData} type="num" />
        ) : (
          <span className={`${CLS}__mom-value`}>-</span>
        )}
      </div>
    </div>
  );
};

const FinanceRow: React.FC<{
  label: string;
  data?: IGggFinanceSummarySection;
}> = ({ label, data }) => (
  <div className="dbs-ltf-ggg-card__row">
    <div className="dbs-ltf-ggg-card__row-label">{label}</div>
    <div className={`${CLS}__total`}>
      <div className={`${CLS}__total-label`}>Total Projects</div>
      <div className={`${CLS}__total-value`}>{data?.total_count ?? '-'}</div>
    </div>
    <MetricBlock
      title="Revenue After Refund"
      value={data?.revenue_after_refund?.value}
      mom={data?.revenue_after_refund?.mom}
      withCurrency
    />
    <MetricBlock
      title="MAU"
      value={data?.mau?.value}
      mom={data?.mau?.mom}
    />
  </div>
);

const FinanceOverviewCard: React.FC<IFinanceOverviewCardProps> = ({
  summary,
  studioCover,
  studioName = 'Grinding Gear Games',
  loading = false,
}) => {
  const [coverSrc, setCoverSrc] = useState(
    studioCover || GameDefault,
  );

  useEffect(() => {
    setCoverSrc(studioCover || GameDefault);
  }, [studioCover]);

  const displayTitle = useMemo(() => {
    if (studioName.includes('Overview')) {
      return studioName;
    }
    return `${studioName} Overview`;
  }, [studioName]);

  return (
    <CardCommon
      cardKey={0}
      isActive={1}
      className="dbs-home-metric-card dbs-ltf-ggg-card"
      title={
        <div className="dbs-ltf-overview-card__title-wrapper">
          <img
            className="dbs-ltf-overview-card__cover"
            src={coverSrc}
            alt={studioName}
            onError={() => setCoverSrc(GameDefault)}
          />
          <span>{displayTitle}</span>
          <IconTipsCom tipString={GGG_OVERVIEW_TIPS} mWidth={520} />
        </div>
      }
    >
      {loading ? (
        <Loading style={{ minHeight: '180px' }} />
      ) : (
        <div className="dbs-ltf-ggg-card__row-wrapper">
          <FinanceRow label="Oversea" data={summary?.oversea} />
          <FinanceRow label="WeGame" data={summary?.wegame} />
        </div>
      )}
    </CardCommon>
  );
};

export default FinanceOverviewCard;
